import { useState, useEffect, useCallback, ChangeEvent, Fragment } from 'react';
import { Search, RefreshCw, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';
import { badgeFor } from '../compras/NodeCostTree';

interface WorkOrder {
  id: string;
  orderNumber: string;
  projectId: string;
  projectExpedientNumber: number;
  projectName: string;
  projectEtiqueta?: string | null;
  sequenceNumber: number;
  name: string;
  orderType: string;
  description?: string;
  productCode?: string;
  prevHours: number;
  realHours: number;
  realPurchases: number;
}

interface OTDetailLine {
  nature?: string;
  childType?: string;
  referencia?: string;
  detail?: string;
  description?: string;
  unit?: string;
  quantity?: number;
  dimensionA?: number;
  dimensionB?: number;
  dimensionH?: number;
  resolvedUnitCost?: number;
  __budgetLineConcepte?: string;
  __lineQty?: number;
}

const formatNumber = (num: number | undefined | null, decimals = 2): string => {
  if (num === undefined || num === null || isNaN(Number(num))) return '0,00';
  return Number(num).toLocaleString('ca-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const detailLineMeasurement = (l: OTDetailLine): number => {
  const qty = Number(l.quantity) || 0;
  const a = Number(l.dimensionA) || 0;
  const b = Number(l.dimensionB) || 0;
  const h = Number(l.dimensionH) || 0;
  if (a > 0 && b > 0 && h > 0) return qty * a * b * h;
  if (a > 0 && b > 0) return qty * a * b;
  if (a > 0) return qty * a;
  return qty;
};

// Per defecte nomes es mostren les OT's de Fabricació i Muntatge — son les
// que es planifiquen i executen al taller; Materials/Serveis es gestionen
// des de Compres i solen quedar tancades abans que calgui consultar-les aquí.
const DEFAULT_TYPE_CODES = ['TRE', 'MUN'];

export default function WorkOrdersList() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [otTypeOpts, setOtTypeOpts] = useState<{ id: number; code: string; name: string }[]>([]);
  const [selectedTypeCodes, setSelectedTypeCodes] = useState<Set<string>>(new Set(DEFAULT_TYPE_CODES));
  const toggleTypeCode = (code: string) => {
    setSelectedTypeCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [detailLines, setDetailLines] = useState<Record<string, OTDetailLine[]>>({});
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());

  const toggleDetail = async (ot: WorkOrder) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ot.id)) next.delete(ot.id); else next.add(ot.id);
      return next;
    });
    if (detailLines[ot.id]) return;
    setDetailLoading((prev) => new Set(prev).add(ot.id));
    try {
      const res = await api.get<{ success: boolean; data: OTDetailLine[] }>(`/work-orders/${ot.id}/detail-lines`);
      setDetailLines((prev) => ({ ...prev, [ot.id]: res.data || [] }));
    } catch {
      setDetailLines((prev) => ({ ...prev, [ot.id]: [] }));
    } finally {
      setDetailLoading((prev) => { const next = new Set(prev); next.delete(ot.id); return next; });
    }
  };

  useEffect(() => {
    api.get<{ success: boolean; data: any[] }>('/work-orders/types').then((res) => {
      setOtTypeOpts((res.data ?? []).filter((t: any) => t.isActive));
    }).catch(() => {});
  }, []);

  const fetchWorkOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get<{ success: boolean; data: any[] }>(
        `/work-orders?${params.toString()}`
      );
      if (response.success && Array.isArray(response.data)) {
        const normalized: WorkOrder[] = response.data.map((wo: any) => ({
          id: wo.id,
          orderNumber: wo.orderNumber || wo.order_number || '',
          projectId: wo.projectId || wo.project_id || '',
          // L'endpoint /work-orders (listWorkOrders) retorna el projecte
          // niat (leftJoinAndSelect), no camps plans expedient_number/
          // project_name.
          projectExpedientNumber: Number(wo.project?.expedientNumber ?? wo.project?.expedient_number ?? 0),
          projectName: wo.project?.name ?? '',
          projectEtiqueta: wo.project?.etiquetaExpedient ?? wo.project?.etiqueta_expedient ?? null,
          sequenceNumber: Number(wo.sequenceNumber || wo.sequence_number || 0),
          name: wo.name || '',
          orderType: wo.type || wo.orderType || '',
          description: wo.description,
          productCode: wo.productCode || wo.product_code,
          prevHours: Number(wo.prevHours || wo.prev_hours || 0),
          realHours: Number(wo.realHours || wo.real_hours || 0),
          realPurchases: Number(wo.realPurchases || wo.real_purchases || 0),
        }));
        setWorkOrders(normalized);
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  // Filtre de tipus (multi-selecció) aplicat en client — "Totes" nomes es
  // representa implicitament quan no hi ha cap tipus seleccionat. Ordenat
  // per Num OT descendent (format OT-NNNNN.NNN d'amplada fixa, l'ordre
  // alfabètic ja coincideix amb el numèric).
  const visibleWorkOrders = (selectedTypeCodes.size === 0
    ? workOrders
    : workOrders.filter((ot) => selectedTypeCodes.has(ot.orderType))
  ).slice().sort((a, b) => b.orderNumber.localeCompare(a.orderNumber));

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4">
        {/* Type filter chips (multi-selecció) */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-600">Tipus:</span>
          <button
            onClick={() => setSelectedTypeCodes(new Set())}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              selectedTypeCodes.size === 0
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}
          >
            Totes
          </button>
          {otTypeOpts.map((t) => (
            <button
              key={t.code}
              onClick={() => toggleTypeCode(t.code)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                selectedTypeCodes.has(t.code)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cercar per num OT o expedient..."
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="icon" onClick={fetchWorkOrders}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-muted-foreground">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-2 py-3 w-6"></th>
                <th className="px-3 py-3 text-left font-medium text-slate-600">Num</th>
                <th className="px-3 py-3 text-left font-medium text-slate-600 min-w-[200px]">Expedient</th>
                <th className="px-3 py-3 text-left font-medium text-slate-600 min-w-[250px]">Descripcio</th>
                <th className="px-3 py-3 text-center font-medium text-slate-600">Cod.</th>
                <th className="px-3 py-3 text-right font-medium text-slate-600">Hores</th>
                <th className="px-3 py-3 text-right font-medium text-slate-600">Compres</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    Carregant...
                  </td>
                </tr>
              ) : visibleWorkOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No s&apos;han trobat OT&apos;s
                  </td>
                </tr>
              ) : (
                visibleWorkOrders.map((ot) => {
                  const isExpanded = expandedIds.has(ot.id);
                  const lines = detailLines[ot.id] ?? [];
                  return (
                  <Fragment key={ot.id}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => toggleDetail(ot)} className="p-0.5 rounded hover:bg-slate-100 text-slate-400">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono">
                      {ot.orderNumber}
                    </td>
                    <td className="px-3 py-2">
                      <div>{ot.projectExpedientNumber ? `${ot.projectExpedientNumber} — ` : ''}{ot.projectEtiqueta || ot.projectName}</div>
                      {ot.projectEtiqueta && ot.projectEtiqueta !== ot.projectName && (
                        <div className="text-xs text-slate-400">{ot.projectName}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {ot.name}{ot.description ? ` - ${ot.description}` : ''}
                    </td>
                    <td className="px-3 py-2 text-center">{ot.productCode}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(ot.realHours)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(ot.realPurchases)}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <td colSpan={7} className="px-4 py-3">
                        {detailLoading.has(ot.id) ? (
                          <p className="text-xs text-slate-500">Carregant detall...</p>
                        ) : lines.length === 0 ? (
                          <p className="text-xs text-slate-500">Aquesta OT no té cap línia de detall associada.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="expedient-budget-lines-table w-full">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Tipus</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Línia pressupost</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Ref. Plànol</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Descripció</th>
                                  <th className="px-2 py-1.5 text-left font-medium text-slate-500">Element</th>
                                  <th className="px-2 py-1.5 text-center font-medium text-slate-500">UA</th>
                                  <th className="px-2 py-1.5 text-right font-medium text-slate-500">UN</th>
                                  <th className="px-2 py-1.5 text-right font-medium text-slate-500">Cost/U</th>
                                  <th className="px-2 py-1.5 text-right font-medium text-slate-500">Cost total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lines.map((sub, i) => {
                                  const lineQty = sub.__lineQty ?? 1;
                                  const measurement = detailLineMeasurement(sub) * lineQty;
                                  const costU = Number(sub.resolvedUnitCost || 0);
                                  const costTotal = measurement * costU;
                                  const badge = badgeFor((sub.childType as any) || 'MATERIAL', sub.nature || 'MATERIAL');
                                  return (
                                    <tr key={i} className="border-t border-slate-100">
                                      <td className="px-2 py-1.5">
                                        <span className={`inline-block font-bold px-1 rounded ${badge.cls}`}>{badge.text}</span>
                                      </td>
                                      <td className="px-2 py-1.5 text-slate-500">{sub.__budgetLineConcepte || '—'}</td>
                                      <td className="px-2 py-1.5">{sub.referencia || '—'}</td>
                                      <td className="px-2 py-1.5">{sub.detail || '—'}</td>
                                      <td className="px-2 py-1.5">{sub.description || '—'}</td>
                                      <td className="px-2 py-1.5 text-center text-slate-500">{sub.unit || ''}</td>
                                      <td className="px-2 py-1.5 text-right">{formatNumber(measurement)}</td>
                                      <td className="px-2 py-1.5 text-right">{formatNumber(costU)}</td>
                                      <td className="px-2 py-1.5 text-right font-medium">{formatNumber(costTotal)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
